import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { PADDING } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, KeyboardInput, createHtmlElement, empty, setHTMLElementContent, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny, ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { AcceptInputValueType, CjsInput } from "../../components/inputs/input";
import { CjsEditor, EditorOptions, EditorSetValueOptions, defaultEditorOptions } from "../editor";
import { CjsInputTextEditor } from "../inputTextEditor/inputTextEditor";
import { CjsSelectEditor } from "../selectEditor/selectEditor";

ensureLoaded(CjsInput);

export type EditableTextEditorMode = "read" | "edit";

export interface EditableTextEditorToggleEvent<InputValueType> {

    /** Current mode of EditableTextEditor */
    mode: EditableTextEditorMode;

    /** Value of editor */
    value?: InputValueType;

    /** Text displayed by editor */
    textDisplay: AcceptedHtmlElementContent;
}

export interface EditableTextEditorOptions<InputValueType> extends EditorOptions {

    /** Min height of editor */
    minHeight?: string;

    /** Value to display in placeholder of text value */
    placeholder?: string;

    /** Format text from valueEditor to string */
    textFormatter: (value: InputValueType | undefined) => AcceptedHtmlElementContent;

    /** Display dashed border around component */
    showBorders?: boolean;
}

export const defaultEditableTextEditorOptions: EditableTextEditorOptions<anyReallyAny> = {
    ...defaultEditorOptions,
    minHeight: "24px",

    /** Default formatter */
    textFormatter: (value: anyReallyAny) => value
};

export const TEXT_DISPLAY = "text-display";
export const TEXT_HOVERED = "text-hovered";
export const EDITOR_BORDERED = "cjs-editable-text-editor-bordered";

/**
 * Edit a simple text, with a CervvalJs Editor
 */
@CustomElement({
    name: "cjs-editable-text-editor",
    style: require("./editableTextEditor.scss"),
    template: require("./editableTextEditor.html"),
    elementCssClasses: ["cjs-editor", "inline-block"]
})
export class CjsEditableTextEditor<InputValueType extends AcceptInputValueType = string> extends CjsEditor<InputValueType> {

    @TypedHtmlElements()
    private readonly _components!: {
        valueEditorContainer: HTMLDivElement
    };

    /** Attributes options of InputTextEditor */
    @TypedWebComponentOptions(defaultEditableTextEditorOptions)
    public readonly options!: WebComponentOptions<EditableTextEditorOptions<InputValueType>>;

    /** Shortcut for valueEditor options */
    public get valueEditorOptions(): WebComponentOptions<EditorOptions> {
        return this.getInput().options;
    }

    /** Editor to edit value of text */
    private _valueEditor!: CjsEditor<InputValueType>;

    /** Text displayed */
    private _textValue?: AcceptedHtmlElementContent;

    /**
     * Current mode of EditableTextEditor
     * read: text displayed
     * edit: editor displayed
     */
    private _mode: EditableTextEditorMode = "read";

    /** Text div container */
    private _textDiv = createHtmlElement("div", {
        cssClass: [TEXT_DISPLAY],
        attributes: {
            valref: "validationFormElement"
        },
        innerHTML: this._textValue
    });

    @Event(NoBubblesEventInit)
    public toggleEvent!: EventHandler<EditableTextEditorToggleEvent<InputValueType>>;

    constructor(options?: Partial<EditableTextEditorOptions<InputValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        // Handle not defined editor
        if (!this._valueEditor) {
            this._valueEditor = new CjsInputTextEditor() as unknown as CjsEditor<InputValueType>;
            (this._valueEditor as unknown as CjsInputTextEditor).components.inputText.components.input.classList.add(PADDING.P_0);
        }

        this.options.setWithoutRefresh(options);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        this._initListeners();

        this.append(this._textDiv);
    }

    /** Refresh component display */
    public override refresh(): void {
        // Refresh
        super.refresh();

        this._refreshValueEditor();
        this._refreshBordered();
        this._refreshPlaceholder();
    }

    /** Get value editor */
    public getInput(): CjsEditor<InputValueType> {
        return this._valueEditor;
    }

    /** 
     * Set editor value 
     * @returns true if value changed
     */
    public override setValue(value: InputValueType | undefined, options: EditorSetValueOptions = { dispatchEvents: true, refreshIfChanged: true }): boolean {

        if (this._value !== value) {
            this._value = value;

            this._valueEditor.setValue(this._value);

            // reformat value in input if needed
            this._refreshInputValue();

            if (options.refreshIfChanged) {
                this.refresh();
            }

            if (options.dispatchEvents) {
                this._dispatchValueChangedEvent();
            }
            return true;
        }
        return false;
    }

    /** Refresh value editor */
    private _refreshValueEditor(): void {
        empty(this._components.valueEditorContainer);

        this.append(this._valueEditor);
        this.getInput().changeEvent.on((event: Event) => {
            this._onInputValueChange(event);

            // Dispatch later hide, wait for "click" event to be dispatched
            setTimeout(() => {
                this.hideValueEditor();
            });

        });
        this._valueEditor.setValue(this._value);
        if (this._mode === "read") {
            setDisplay(this._valueEditor, Display.NONE);
        }
    }

    /** Refresh bordered style */
    private _refreshBordered(): void {
        toggleClass(this, this.options.showBorders, EDITOR_BORDERED);
    }

    /** Refresh placeholder */
    private _refreshPlaceholder(): void {
        if (!this._textValue) {
            this._textDiv.innerText = this.options.placeholder ?? "";
        }
    }

    /** Update the textDisplay innerText */
    private _updateTextDisplay(text: AcceptedHtmlElementContent | undefined): void {
        this._textValue = text ?? "";
        this._textDiv.style.minHeight = this.options.minHeight ?? "0px";
        setHTMLElementContent(this._textDiv, text ?? this.options.placeholder ?? "");
    }

    /** Initializes all the listeners of the component */
    private _initListeners(): void {

        this.addEventListener("click", (e: MouseEvent) => {

            if (this._mode === "read" && !this.disabled) {
                this.toggleValueEditor();

                // Is valueEditor is a select, open select editor if not already open
                if (this._valueEditor instanceof CjsSelectEditor && !this._valueEditor.smartSelect.isOpen()) {
                    this._valueEditor.smartSelect.open();
                }
            }

            e.stopPropagation();// Required to not close on click

        });

        this.addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER && this._mode === "edit") {
                this.hideValueEditor();
            }
        });

        document.addEventListener("click", this._onDocumentClick);
    }

    /** When component is removed from dom */
    public disconnectedCallback(): void {
        document.removeEventListener("click", this._onDocumentClick);
    }

    /** When click is trigger on document */
    private _onDocumentClick = (): void => {
        if (this._mode === "edit") {
            this.hideValueEditor();
        }
    };

    /**
     * Set value editor
     */
    public setValueEditor(editor: CjsEditor<InputValueType>, refresh = true): void {
        this._valueEditor = editor;

        if (refresh) {
            this.refresh();
        }

    }

    /** Show value editor */
    public toggleValueEditor(mode?: EditableTextEditorMode): void {
        if (mode === this._mode) {
            return; // Don't toggle, already current mode
        }

        const modeToToggle = mode ?? this._mode;

        if (modeToToggle === "edit") {
            this.hideValueEditor();
        } else {
            this.showValueEditor();
        }

    }

    /** Show value editor */
    public showValueEditor(): void {
        this._mode = "edit";

        this._valueEditor.style.maxHeight = toPx(this.clientHeight);
        this._valueEditor.style.minWidth = toPx(this.clientWidth);
        this._valueEditor.focus();

        // refresh display
        setDisplay(this._valueEditor, Display.FLEX);
        setDisplay(this._textDiv, Display.NONE);

        const input = this._valueEditor.querySelector("input");
        // Focus input if one is present
        if (input) {
            input.focus();
        }

        // Emit event
        this._emitToggleEvent();
    }

    /** Hide value editor */
    public hideValueEditor(): void {
        this._mode = "read";

        setDisplay(this._valueEditor, Display.NONE);
        setDisplay(this._textDiv, Display.FLEX);

        // refresh input
        this._refreshInputValue();
        this._refreshPlaceholder();

        // Emit event
        this._emitToggleEvent();
    }

    /** Emit toggle event */
    private _emitToggleEvent(): void {
        this.toggleEvent.emit({
            mode: this._mode,
            value: this._value,
            textDisplay: this._textValue ?? ""
        });
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        const text = this.options.textFormatter(this._getValueFromInputComponent());
        this._updateTextDisplay(text);
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): InputValueType | undefined {
        return this.getInput().getValue();
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.valueEditorOptions.setAndRefreshIfChanged({ disabled: this.disabled });
        toggleAttribute(this, this.disabled, "disabled", "");
    }
}